/**
 * Google Drive.
 *
 * Biggest free tier of the three, with two things worth knowing.
 *
 * The scope is `drive.file`, which Google classes as non-sensitive: it grants
 * access only to files this app itself created, needs no verification round and
 * no security assessment, and can never see the rest of someone's Drive. Any
 * broader Drive scope would drag in an annual audit for no benefit here.
 *
 * The catch is durability. Google revokes refresh tokens after roughly six
 * months of non-use, so a Drive connection left completely alone will
 * eventually stop working. Fine while the owner is signing in; a real problem
 * for an unattended switch, which is why Dropbox leads instead.
 */
import type { OAuthApp } from '../oauth';
import type { ProviderMeta, StorageBackend } from './types';
import { CLIENT_IDS } from '../providerConfig';

const APP: OAuthApp = {
  clientId: CLIENT_IDS.googledrive,
  authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
  tokenUrl: 'https://oauth2.googleapis.com/token',
  scopes: ['https://www.googleapis.com/auth/drive.file'],
  extraAuthParams: {
    // Without both of these Google returns an access token and no refresh
    // token, and the connection silently becomes single-session.
    access_type: 'offline',
    prompt: 'consent',
    include_granted_scopes: 'true',
  },
};

export const googleDriveMeta: ProviderMeta = {
  id: 'googledrive',
  name: 'Google Drive',
  blurb: 'Sign in once. Only ever sees files this page created.',
  free: '15 GB free',
  auth: 'oauth',
  durability: {
    lasts: 'months',
    note: 'Google revokes the connection after about six months without use.',
  },
  app: APP,
  needsSetup: !CLIENT_IDS.googledrive,
};

export function googleDriveApp(): OAuthApp {
  return APP;
}

const FOLDER = 'AliveGuardian';
const FOLDER_MIME = 'application/vnd.google-apps.folder';

export function googleDriveBackend(accessToken: string): StorageBackend {
  const auth = { Authorization: `Bearer ${accessToken}` };

  /**
   * Drive has no paths — files are found by query. Names are not unique either,
   * so every lookup filters to our own folder and takes the newest match.
   */
  async function findFile(name: string, parentId: string): Promise<string | null> {
    const q = `name='${escapeQ(name)}' and '${parentId}' in parents and trashed=false`;
    const url =
      'https://www.googleapis.com/drive/v3/files' +
      `?q=${encodeURIComponent(q)}&fields=files(id)&orderBy=createdTime desc&pageSize=1`;
    const res = await fetch(url, { headers: auth });
    if (!res.ok) throw new Error(await describe(res));
    const j = (await res.json()) as { files?: { id: string }[] };
    return j.files?.[0]?.id ?? null;
  }

  async function folderId(): Promise<string> {
    const q = `name='${FOLDER}' and mimeType='${FOLDER_MIME}' and trashed=false`;
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)&pageSize=1`,
      { headers: auth }
    );
    if (!res.ok) throw new Error(await describe(res));
    const found = ((await res.json()) as { files?: { id: string }[] }).files?.[0]?.id;
    if (found) return found;

    const made = await fetch('https://www.googleapis.com/drive/v3/files?fields=id', {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: FOLDER, mimeType: FOLDER_MIME }),
    });
    if (!made.ok) throw new Error(await describe(made));
    return ((await made.json()) as { id: string }).id;
  }

  return {
    async put(path, body) {
      const parent = await folderId();
      const existing = await findFile(path, parent);

      // Multipart upload: one part metadata, one part bytes. Update keeps the
      // same file id so a re-save does not litter Drive with duplicates.
      const boundary = `agl${crypto.randomUUID().replace(/-/g, '')}`;
      const meta = existing ? { name: path } : { name: path, parents: [parent] };
      const head = new TextEncoder().encode(
        `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n` +
          `${JSON.stringify(meta)}\r\n--${boundary}\r\nContent-Type: application/octet-stream\r\n\r\n`
      );
      const tail = new TextEncoder().encode(`\r\n--${boundary}--`);
      const payload = new Uint8Array(head.length + body.length + tail.length);
      payload.set(head, 0);
      payload.set(body, head.length);
      payload.set(tail, head.length + body.length);

      const url =
        'https://www.googleapis.com/upload/drive/v3/files' +
        (existing ? `/${existing}?uploadType=multipart&fields=size` : '?uploadType=multipart&fields=size');

      const res = await fetch(url, {
        method: existing ? 'PATCH' : 'POST',
        headers: { ...auth, 'Content-Type': `multipart/related; boundary=${boundary}` },
        body: payload as BodyInit,
      });
      if (!res.ok) throw new Error(await describe(res));
      const j = (await res.json()) as { size?: string };
      return { size: j.size ? Number(j.size) : body.byteLength };
    },

    async get(path) {
      const id = await findFile(path, await folderId());
      if (!id) return null;
      const res = await fetch(`https://www.googleapis.com/drive/v3/files/${id}?alt=media`, {
        headers: auth,
      });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(await describe(res));
      return new Uint8Array(await res.arrayBuffer());
    },

    async remove(path) {
      const id = await findFile(path, await folderId());
      if (!id) return;
      const res = await fetch(`https://www.googleapis.com/drive/v3/files/${id}`, {
        method: 'DELETE',
        headers: auth,
      });
      if (!res.ok && res.status !== 404) throw new Error(await describe(res));
    },
  };
}

/** Drive query strings are single-quoted, so an apostrophe would break out. */
function escapeQ(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

async function describe(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const j = JSON.parse(text) as { error?: { message?: string } };
    if (j.error?.message) return `Google Drive: ${j.error.message}`;
  } catch {
    /* not JSON */
  }
  return `Google Drive returned ${res.status}${text ? ` — ${text.slice(0, 120)}` : ''}`;
}
